import { motion } from "framer-motion";
import { Building, Building2, Plus, Tag } from "lucide-react";
import React from "react";
import { UserRole } from "../../../types/user";
import NewBranchModal from "../modals/NewBranchModal";
import NewInstitutionModal from "../modals/NewInstitutionModal";
import NewServiceCategoryModal from "../modals/NewServiceCategoryModal";

interface InstitutionFormProps {
  formData: any;
  selectedRole: UserRole;
  institutionData: any;
  showNewInstitutionForm: boolean;
  showNewBranchForm: boolean;
  showNewCategoryForm: boolean;
  onInstitutionChange: (institutionId: string) => void;
  onInputChange: (field: string, value: any) => void;
  setShowNewInstitutionForm: (show: boolean) => void;
  setShowNewBranchForm: (show: boolean) => void;
  setShowNewCategoryForm: (show: boolean) => void;
  onCreateInstitution: (formData: FormData) => void;
  onCreateBranch: (formData: FormData) => void;
  onCreateServiceCategory: (formData: FormData) => void;
}

const InstitutionForm: React.FC<InstitutionFormProps> = ({
  formData,
  selectedRole,
  institutionData,
  showNewInstitutionForm,
  showNewBranchForm,
  showNewCategoryForm,
  onInstitutionChange,
  onInputChange,
  setShowNewInstitutionForm,
  setShowNewBranchForm,
  setShowNewCategoryForm,
  onCreateInstitution,
  onCreateBranch,
  onCreateServiceCategory,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="text-center">
        <h3 className="text-lg font-medium text-gray-900">
          Institution & Branch Assignment
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Select the institution and branch for this user
        </p>
      </div>

      <div className="space-y-4">
        {/* Institution Selection */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Institution
            </label>
            <button
              type="button"
              onClick={() => setShowNewInstitutionForm(true)}
              className="text-sm text-primary-600 hover:text-primary-500 flex items-center"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add New
            </button>
          </div>
          <div className="relative">
            <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <select
              value={formData.institutionId}
              onChange={(e) => onInstitutionChange(e.target.value)}
              className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              required
            >
              <option value="">Select Institution</option>
              {institutionData.institutions.map((institution: any) => (
                <option key={institution.id} value={institution.id}>
                  {institution.institutionName} - {institution.institutionCode}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Branch Selection */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Branch
            </label>
            {formData.institutionId && (
              <button
                type="button"
                onClick={() => setShowNewBranchForm(true)}
                className="text-sm text-primary-600 hover:text-primary-500 flex items-center"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add New
              </button>
            )}
          </div>
          <div className="relative">
            <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <select
              value={formData.branchId}
              onChange={(e) => onInputChange("branchId", e.target.value)}
              className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              disabled={!formData.institutionId}
              required
            >
              <option value="">Select Branch</option>
              {institutionData.branches.map((branch: any) => (
                <option key={branch.id} value={branch.id}>
                  {branch.branchName} - {branch.branchCode}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Service Category Selection (for service providers) */}
        {(selectedRole === "stylist" || selectedRole === "receptionist") && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Service Category
              </label>
              <button
                type="button"
                onClick={() => setShowNewCategoryForm(true)}
                className="text-sm text-primary-600 hover:text-primary-500 flex items-center"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add New
              </button>
            </div>
            <div className="relative">
              <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <select
                value={formData.serviceCategoryId}
                onChange={(e) =>
                  onInputChange("serviceCategoryId", e.target.value)
                }
                className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                required
              >
                <option value="">Select Service Category</option>
                {institutionData.serviceCategories.map((category: any) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showNewInstitutionForm && (
        <NewInstitutionModal
          onClose={() => setShowNewInstitutionForm(false)}
          onSubmit={onCreateInstitution}
        />
      )}

      {showNewBranchForm && (
        <NewBranchModal
          onClose={() => setShowNewBranchForm(false)}
          onSubmit={onCreateBranch}
        />
      )}

      {showNewCategoryForm && (
        <NewServiceCategoryModal
          onClose={() => setShowNewCategoryForm(false)}
          onSubmit={onCreateServiceCategory}
          serviceCategories={institutionData.serviceCategories}
        />
      )}
    </motion.div>
  );
};

export default InstitutionForm;
